import { ModeViewType, mapboxStyle } from "constants/map.const";
import { SearchPlace, SelectMapType, SwitchModeView } from "../curb-zone/components";
import { Map as StaticMap, ViewState } from "react-map-gl/mapbox";
import { UrbanFeature, UrbanFeatureMode, UrbanFeatureModeType } from "./urban-feature.types";
import {
  UrbanFeatureModalState,
  crosswalkModes,
  cursorCell,
  initUrbanFeature,
  urbanFeaturePolygonEditModes,
} from "./urban-feature.const";
import { ViewportPlaceType, interactionStateType } from "types/map.type";
import { useContext, useMemo, useState } from "react";

import { CityContext } from "@/contexts/city-context-provider";
import DeckGL from "@deck.gl/react";
import { MAPBOX_ACCESS_TOKEN } from "constants/mapbox.const";
import { MapToolBox } from "@/components/map-tool-box";
import { PickingInfo } from "deck.gl";
import { Point } from "geojson";
import { TrafficLightModal } from "./components/traffic-light-modal";
import { UrbanFeatureDetail } from "./components/urban-feature-detail";
import { UrbanFeatureFilter } from "@/modules/urban-feature/components/urban-feature-filter";
import { UrbanFeaturePolygonModal } from "./components/urban-feature-polygon-modal";
import { UrbanFeatureTooltip } from "./components/urban-feature-tooltip";
import { UrbanFeatureType } from "@/types/gql/graphql";
import { getAddressByPoint } from "@/services/web/mapbox.service";
import { urbanFeatureToolboxIcons } from "@/modules/urban-feature/components/urban-feature-tool-box-entry.const";
import { useSearchFilterUrbanFeature } from "@/modules/urban-feature/hooks/use-search-filter-urban-feature";
import { useUrbanFeaturePoint } from "./hooks/use-urban-feature-point";
import { useUrbanFeaturePolygon } from "./hooks/use-urban-feature-polygon";

export const UrbanFeatureMap = () => {
  const [modeView, setModeView] = useState<ModeViewType>("normal");
  const mapStyle = useMemo(() => mapboxStyle[modeView], [modeView]);
  const [urbanFeatureMode, setUrbanFeatureMode] = useState<UrbanFeatureMode>(UrbanFeatureModeType.View);
  const { currentCity } = useContext(CityContext);
  const { filter, changeFilter } = useSearchFilterUrbanFeature();

  const {
    urbanFeatureMapLayers,
    currentPosition,
    getUrbanFeatures,
    preZooming,
    urbanFeaturePointDetail,
    setUrbanFeaturePointDetail,
    viewport,
    setViewport,
    urbanFeaturePointModalState,
    setUrbanFeaturePointModalState,
    saveUrbanFeaturePoint,
    handleRemoveUrbanFeature,
    hoverUrbanFeaturePointInfo,
    handleClose,
    mapType,
    setMapType,
  } = useUrbanFeaturePoint({ urbanFeatureMode, selectedUrbanTypes: filter.selectedUrbanTypes });

  const {
    openUrbanFeaturePolygonModal,
    setOpenUrbanFeaturePolygonModal,
    urbanFeaturePolygonDetail,
    setUrbanFeaturePolygonDetail,
    cancelUrbanFeaturePolygonChange,
    urbanFeaturePolygonLayers,
    saveUrbanFeaturePolygon,
    hoverUrbanFeaturePolygonInfo,
    handleRemoveUrbanFeaturePolygon,
  } = useUrbanFeaturePolygon({ urbanFeatureMode, selectedUrbanTypes: filter.selectedUrbanTypes });

  const deselectUrbanFeature = () => {
    if (urbanFeaturePointDetail) {
      handleClose();
    }
    setUrbanFeaturePolygonDetail(null);
  };

  const onDeleteUrbanFeature = async () => {
    if (urbanFeaturePolygonEditModes.includes(urbanFeatureMode)) {
      await handleRemoveUrbanFeaturePolygon();
    } else {
      await handleRemoveUrbanFeature();
    }
  };

  const mergedLayer = [...urbanFeatureMapLayers, ...urbanFeaturePolygonLayers];

  const getCursor = () => {
    if (cursorCell.includes(urbanFeatureMode)) {
      return () => "cell";
    }
    return ({ isDragging }: { isDragging: boolean }) => (isDragging ? "grabbing" : "grab");
  };

  const handleDragEnd = (event: PickingInfo) => {
    if (!event.viewport) {
      return;
    }
    const viewport = event.viewport as unknown as ViewState;
    const newPosition = [viewport?.longitude, viewport?.latitude];
    currentPosition.current = [...newPosition];
    getUrbanFeatures(viewport);
  };

  const handleZooming = (input: interactionStateType) => {
    if (preZooming.current && !input.isZooming) {
      getUrbanFeatures(preZooming.current);
    }
  };

  const onViewStateChange = (viewState: ViewState) => {
    preZooming.current = viewState;
  };

  const selectAddress = (viewport: ViewportPlaceType | null) => {
    if (viewport) {
      const geometry = { type: "Point", coordinates: [Number(viewport.longitude), Number(viewport.latitude)] } as Point;
      setUrbanFeaturePointDetail(
        (prevState) =>
          ({
            ...prevState,
            geometry,
          }) as UrbanFeature,
      );

      setViewport((prevState) => ({ ...prevState, ...viewport, zoom: 16 }));
    }
  };

  const handleSelectUrbanFeature = async (info: PickingInfo) => {
    if (
      urbanFeatureMode === UrbanFeatureModeType.AddTrafficLight &&
      urbanFeaturePointModalState === UrbanFeatureModalState.DEACTIVATE
    ) {
      //Add a new traffic light
      const newPoint = { type: "Point", coordinates: info.coordinate } as Point;
      const address = await getAddressByPoint(newPoint);
      setUrbanFeaturePointDetail({
        ...initUrbanFeature,
        address,
        geometry: newPoint,
      });
      setUrbanFeaturePointModalState(UrbanFeatureModalState.SHOW);
    } else if (urbanFeaturePointModalState === UrbanFeatureModalState.MINIMIZE && info.coordinate) {
      //for the traffic light, select a point in the map to get the location
      setUrbanFeaturePointDetail(
        (prevState) =>
          ({
            ...prevState,
            geometry: { type: "Point", coordinates: info.coordinate },
          }) as UrbanFeature,
      );
    } else if (info.object) {
      const isPolygonEditable = (() => {
        if (urbanFeatureMode === UrbanFeatureModeType.EditCrosswalk) {
          return info.object?.properties?.type === UrbanFeatureType.Crosswalk;
        }
        if (urbanFeatureMode === UrbanFeatureModeType.EditSpeedBump) {
          return info.object?.properties?.type === UrbanFeatureType.SpeedBump;
        }
        return false;
      })();

      if (isPolygonEditable) {
        setUrbanFeaturePolygonDetail(info.object);
      }
    } else {
      /**
       * If the user clicks outside an urban feature, deselect an urban feature
       */
      if (urbanFeaturePointDetail?.id) {
        setUrbanFeaturePointDetail(null);
      }
      if (urbanFeaturePolygonDetail?.id) {
        setUrbanFeaturePolygonDetail(null);
      }
    }
  };

  const openUrbanFeatureModal = () => {
    if (urbanFeaturePointDetail?.type === UrbanFeatureType.TrafficLight) {
      setUrbanFeaturePointModalState(UrbanFeatureModalState.SHOW);
    } else {
      setOpenUrbanFeaturePolygonModal(true);
    }
  };

  const selectedUrbanFeature = useMemo(() => {
    return urbanFeaturePointDetail ?? urbanFeaturePolygonDetail?.properties;
  }, [urbanFeaturePointDetail, urbanFeaturePolygonDetail]);

  const hoverInfo = useMemo(() => {
    return hoverUrbanFeaturePointInfo ?? hoverUrbanFeaturePolygonInfo;
  }, [hoverUrbanFeaturePointInfo, hoverUrbanFeaturePolygonInfo]);

  return (
    <div className="relative h-full">
      <DeckGL
        initialViewState={{ ...viewport }}
        controller={{
          touchZoom: true,
          touchRotate: true,
        }}
        getCursor={getCursor()}
        layers={mergedLayer}
        pickingRadius={12}
        onDragEnd={handleDragEnd}
        onInteractionStateChange={handleZooming}
        onClick={handleSelectUrbanFeature}
        onViewStateChange={({ viewState }) => onViewStateChange(viewState as ViewState)}
      >
        <StaticMap mapboxAccessToken={MAPBOX_ACCESS_TOKEN} mapStyle={mapStyle} />
        {urbanFeaturePointModalState !== UrbanFeatureModalState.MINIMIZE && hoverInfo?.urbanFeature && (
          <UrbanFeatureTooltip left={hoverInfo.left} top={hoverInfo.top} urbanFeature={hoverInfo.urbanFeature} />
        )}
      </DeckGL>
      <div className="absolute top-16 left-6 z-6 flex gap-2">
        <SearchPlace onSelect={selectAddress} coordinates={viewport} />
        <SelectMapType mapType={mapType} onSelectMapType={(value) => setMapType(value)} />
        {currentCity && <UrbanFeatureFilter selectedUrbanTypes={filter.selectedUrbanTypes} onChange={changeFilter} />}
      </div>
      <SwitchModeView modeView={modeView} selectModeView={(mode: ModeViewType) => setModeView(mode)} />
      <div className="absolute top-60 left-8 z-6">
        <MapToolBox
          appMode={urbanFeatureMode}
          setAppMode={setUrbanFeatureMode}
          toolboxIcons={urbanFeatureToolboxIcons}
        />
      </div>
      {urbanFeaturePointModalState !== UrbanFeatureModalState.DEACTIVATE && urbanFeaturePointDetail && (
        <TrafficLightModal
          coordinates={viewport}
          urbanFeature={urbanFeaturePointDetail}
          onClose={handleClose}
          onSave={saveUrbanFeaturePoint}
          urbanFeatureModalState={urbanFeaturePointModalState}
          setUrbanFeatureModalState={setUrbanFeaturePointModalState}
          setUrbanFeatureDetail={setUrbanFeaturePointDetail}
        />
      )}

      {urbanFeaturePointModalState !== UrbanFeatureModalState.MINIMIZE &&
        selectedUrbanFeature &&
        selectedUrbanFeature?.id && (
          <UrbanFeatureDetail
            setUrbanFeatureModalState={openUrbanFeatureModal}
            urbanFeature={selectedUrbanFeature}
            removeUrbanFeature={onDeleteUrbanFeature}
            onClose={deselectUrbanFeature}
          />
        )}
      {openUrbanFeaturePolygonModal && urbanFeaturePolygonDetail && (
        <UrbanFeaturePolygonModal
          urbanFeature={urbanFeaturePolygonDetail?.properties}
          onClose={cancelUrbanFeaturePolygonChange}
          onSave={saveUrbanFeaturePolygon}
          title={crosswalkModes.includes(urbanFeatureMode) ? "Crosswalk" : "Speed bump"}
        />
      )}
    </div>
  );
};
